import React, { FC } from 'react';

import {
  Tab,
  Text,
  Tabs,
  Flex,
  Modal,
  Button,
  TabList,
  TabPanels,
  TabPanel,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Heading,
  Icon,
  Spinner,
} from '@chakra-ui/react';

import { EditForm } from './components/EditForm';
import { Pictures } from './components/Pictures';
import { GeneralInfo } from './components/GeneralInfo';
import { DeletePickupButton } from './components/DeletePickupButton';

import { EditPickupModalViewProps } from './EditPickupModal.props';
import { HarvestCounts } from './components/HarvestCounts';
import { Breakdown } from './components/BreakDownAPI';
import { MdLocalActivity, MdLocalShipping } from 'react-icons/md';
import { convertUTCToLocal } from 'utils/dateUtils';
import DirtySvg from 'svg/DirtySvg';
import { packages } from 'types/pickup';
import PickupDetailsPDFGenerator from 'routes/portal/Pickups/PickupDetailsPDFGenerator';
import { useSKU } from 'hooks';
import { washHub } from 'store';

const tabProps = {
  opacity: 0.5,
  fontWeight: '600',
  color: 'support.500',
  borderBottom: '2px solid',
  borderColor: 'primary.500',
};

const px = { base: '20px', md: '100px' };

const EditPickupModalView: FC<EditPickupModalViewProps> = (props) => {
  const {
    pickupToShow,
    isOpen,
    onClose,
    onUpdate,
    onDelete,
    isDeleting,
    pickup,
    isUpdating,
    collectionPoints,
    sites,
    isEditing,
    setIsEditing,
    renderNextPickupButton,
    renderPreviousPickupButton,
    isLoadingPickup,
  } = props;

  const OpenedPackages = pickup?.pickedUpStats ?? [];
  const totalPackagesMap: { [sku: string]: any } = {};
  const addOrUpdatePackage = (pkg: packages, source: string) => {
    const { sku, quantity, ...rest } = pkg;
    if (!totalPackagesMap[sku] && sku !== undefined) {
      totalPackagesMap[sku] = {
        sku,
        openedQuantity: 0,
        sealedQuantity: 0,
        ...rest,
      };
    }
    if (source === 'opened' && sku !== undefined) {
      totalPackagesMap[sku].openedQuantity += quantity;
    }
  };

  OpenedPackages.forEach((pkg) => addOrUpdatePackage(pkg, 'opened'));
  const totalPackages = Object.values(totalPackagesMap);
  const totalOpenedPackages = totalPackages.reduce(
    (acc, pkg) => acc + (isNaN(pkg.openedQuantity) ? 0 : pkg.openedQuantity),
    0,
  );

  const { data: skuData, isLoading: isSkuLoading } = useSKU(
    'item',
    undefined,
    pickup?.site?._id,
    1,
    true,
  );

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const phcMethod = selectedWashHub?.phcMethod ?? 0;
  // eslint-disable-next-line eqeqeq
  const isRacked = phcMethod == 0;

  return (
    <Modal
      size="5xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="10px" display="relative">
        {!isEditing && pickup && renderNextPickupButton(pickup)}
        {!isEditing && pickup && renderPreviousPickupButton(pickup)}
        {!isEditing && (
          <ModalCloseButton
            _focus={{ outline: 'none' }}
            isDisabled={isUpdating}
            top="40px"
            right="30px"
            size="20px"
          />
        )}

        {isSkuLoading && (
          <Flex justify="center" align="center" h="80vh">
            <Spinner />
          </Flex>
        )}

        {pickup && !isSkuLoading && (
          <>
            {isEditing ? (
              <ModalBody p="0">
                <EditForm
                  pickup={pickup}
                  onCancel={() => setIsEditing(false)}
                  onUpdate={onUpdate}
                  isUpdating={isUpdating}
                  collectionPoints={collectionPoints}
                  sites={sites}
                />
              </ModalBody>
            ) : (
              <ModalBody p="0">
                <Flex
                  shadow="lg"
                  w="100%"
                  py="30px"
                  align="center"
                  justify="space-between"
                  px={px}>
                  <Heading fontSize="24px" fontWeight="600">
                    Pickup Details
                  </Heading>

                  <Flex gap="10px">
                    {pickupToShow && (
                      <PickupDetailsPDFGenerator
                        pickup={pickupToShow}
                        skuData={skuData ?? []}
                        isRacked={isRacked}
                      />
                    )}
                    <DeletePickupButton
                      onDelete={onDelete}
                      isDeleting={isDeleting}
                    />
                    <Button
                      isDisabled={isDeleting}
                      onClick={() => setIsEditing(true)}>
                      Edit Pickup
                    </Button>
                  </Flex>
                </Flex>
                <Flex mt="20px" justify="center" align="center" width="100%">
                  <Flex width="60%" justify="space-around">
                    <Flex direction="column" fontSize="16px" align="center">
                      <Flex align="center" gap="5px">
                        <MdLocalActivity size="22px" color="#834EF3" />
                        <Text fontWeight="600">Site</Text>
                      </Flex>
                      <Text>{pickupToShow?.site?.name ?? ''}</Text>
                    </Flex>

                    <Flex direction="column" fontSize="16px" align="center">
                      <Flex align="center" gap="5px">
                        <Icon
                          as={MdLocalShipping}
                          color="#965AB1"
                          boxSize={6}
                        />
                        <Text fontWeight="600">Pickup Date</Text>
                      </Flex>
                      <Text>
                        {convertUTCToLocal(pickupToShow?.date, true, true) ||
                          ''}
                      </Text>
                    </Flex>

                    <Flex direction="column" fontSize="16px" align="center">
                      <Flex align="center" gap="5px">
                        <DirtySvg />
                        <Text fontWeight="600">Total Dirty</Text>
                      </Flex>
                      <Text>{totalOpenedPackages || '-'}</Text>
                    </Flex>
                  </Flex>
                </Flex>

                <Tabs pb="20px" isLazy>
                  <TabList pt="20px" border="none" justifyContent="center">
                    <Tab {...tabProps}>General Info</Tab>
                    <Tab {...tabProps}>Pickup Verification</Tab>
                    <Tab {...tabProps}>Harvest Counts</Tab>
                    <Tab {...tabProps}>Breakdown KPI</Tab>
                  </TabList>

                  <TabPanels px={px} minH="440px">
                    <TabPanel px="0" h="100%">
                      <GeneralInfo pickup={pickupToShow} />
                    </TabPanel>
                    <TabPanel px="0">
                      <Pictures
                        isLoading={isLoadingPickup}
                        pickup={pickupToShow}
                      />
                    </TabPanel>
                    <TabPanel px="0">
                      <HarvestCounts
                        isLoading={isLoadingPickup}
                        pickup={pickupToShow}
                      />
                    </TabPanel>
                    <TabPanel px="0">
                      <Breakdown
                        pickup={pickupToShow}
                        onUpdate={onUpdate}
                        isUpdating={isUpdating}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </ModalBody>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EditPickupModalView;
